import RightArrow from "@/assets/colorable/right_arrow.svg?react";
import RightChevron from "@/assets/colorable/right_chevron_slider.svg?react";
import TikTokLogo from "@/assets/colorable/tiktok.svg?react";
import Step1_de from "@/assets/new_feature_steps/step_1_de.webp";
import Step1_en from "@/assets/new_feature_steps/step_1_en.webp";
import Step2 from "@/assets/new_feature_steps/step_2.webp";
import Step3 from "@/assets/new_feature_steps/step_3.webp";
import Step4 from "@/assets/new_feature_steps/step_4.webp";
import Step5 from "@/assets/new_feature_steps/step_5.webp";
import Button from "@/components/Common/Button";
import clsxm from "@/lib/clsxm";
import debounce from "lodash/debounce";
import {useCallback, useRef, useState, type FC, type WheelEvent} from "react";
import {useTranslation} from "react-i18next";
import Slider, {type Settings} from "react-slick";
import {useMedia} from "react-use";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";

const settings: Settings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  adaptiveHeight: false,
  arrows: false,
  autoplay: true,
  autoplaySpeed: 5000,
  pauseOnHover: true,
  waitForAnimate: true,
};

const NewFeatureSection: FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const {t, i18n} = useTranslation();
  const isTall = useMedia("(min-height: 1200px)");
  const sliderRef = useRef<Slider>(null);

  const handleSideScroll = useCallback(
    debounce((e: WheelEvent<HTMLDivElement>) => {
      if (!sliderRef.current) return;

      if (e.deltaX > 0) sliderRef.current.slickNext();
      else if (e.deltaX < 0) sliderRef.current.slickPrev();
    }, 20),
    [],
  );

  return (
    <div
      className={
        "bg-primary-light-solid col-span-2 flex flex-col items-center gap-10 overflow-hidden py-24 2xl:gap-16 " +
        (isTall ? "justify-center" : "justify-start")
      }>
      <div className="bg-primary-midnight flex h-11 w-60 items-center justify-center rounded-md text-xl font-medium text-white uppercase">
        {t("newFeature")}
      </div>
      <div className="text-primary-midnight w-[400px] text-center text-[44px] leading-[55px] font-bold 2xl:text-[50px]">
        <TikTokLogo className="mr-3 mb-2 inline-block h-8 w-auto shrink-0 2xl:mb-3.5" />
        <span className="font-domaine-display">{t("newFeatureTitle")}</span>
      </div>
      <span className="text-neutral-black w-full text-center text-xl font-semibold">{t("howItWorks")}</span>
      <div className="relative -mt-8 flex w-full items-center justify-center" onWheel={handleSideScroll}>
        <div
          className="absolute top-5 right-3 z-1000 flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white"
          onClick={() => {
            sliderRef.current?.slickNext();
          }}>
          <RightChevron className="text-primary-medium-solid h-2 w-auto shrink-0" />
        </div>

        <div
          className="absolute top-5 left-3 z-1000 flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white"
          onClick={() => {
            sliderRef.current?.slickPrev();
          }}>
          <RightChevron className="text-primary-medium-solid h-2 w-auto shrink-0 rotate-180" />
        </div>
        <Slider
          ref={sliderRef}
          className="h-fit w-full"
          beforeChange={(_, nextSlide) => setCurrentIndex(nextSlide)}
          customPaging={i => (
            <div
              className={clsxm(
                "bg-bold-blue-100 hover:bg-primary-midnight h-3 w-3 rounded-full transition-all duration-150 hover:scale-105",
                i === currentIndex && "bg-primary-dark-solid",
              )}
              key={i}></div>
          )}
          {...settings}>
          <div className="flex flex-col items-center justify-center gap-10 px-12 py-3">
            <div className="border-primary-light-solid z-10 flex items-center justify-center gap-2 rounded-md border bg-white px-3 py-2 shadow-lg drop-shadow-md">
              <span className="font-domaine-display flex items-center justify-center bg-linear-(--primary-dark-gradient) bg-clip-text text-xl leading-none font-bold text-transparent">
                1
              </span>
              <span className="text-neutral-black flex items-center justify-center text-xs font-bold">
                {t("newFeatureStepOne")}
              </span>
            </div>
            <img
              src={i18n.language.includes("en") ? Step1_en : Step1_de}
              alt="New feature step 1"
              className="max-w-96"
            />
          </div>
          <div className="flex flex-col items-center justify-center gap-10 px-12 py-3">
            <div className="border-primary-light-solid z-10 flex items-center justify-center gap-2 rounded-md border bg-white px-3 py-2 shadow-lg drop-shadow-md">
              <span className="font-domaine-display flex items-center justify-center bg-linear-(--primary-dark-gradient) bg-clip-text text-xl leading-none font-bold text-transparent">
                2
              </span>
              <span className="text-neutral-black flex items-center justify-center text-xs font-bold">
                {t("newFeatureStepTwo")}
              </span>
            </div>
            <img src={Step2} alt="New feature step 2" className="max-w-96" />
            <Button
              onClick={() => window.open("https://ads.tiktok.com/help/article/spark-ads", "_blank")}
              buttonStyle="Blue"
              className="flex items-center gap-2.5 px-6 py-4 text-base font-semibold">
              {t("learnMoreImperative")}
              <RightArrow className="h-3 w-3 shrink-0" />
            </Button>
          </div>
          <div className="flex flex-col items-center justify-center gap-10 px-12 py-3">
            <div className="border-primary-light-solid z-10 flex items-center justify-center gap-2 rounded-md border bg-white px-3 py-2 shadow-lg drop-shadow-md">
              <span className="font-domaine-display flex items-center justify-center bg-linear-(--primary-dark-gradient) bg-clip-text text-xl leading-none font-bold text-transparent">
                3
              </span>
              <span className="text-neutral-black flex items-center justify-center text-xs font-bold">
                {t("newFeatureStepThree")}
              </span>
            </div>
            <img src={Step3} alt="New feature step 3" className="max-w-96" />
          </div>
          <div className="flex flex-col items-center justify-center gap-10 px-12 py-3">
            <div className="border-primary-light-solid z-10 flex items-center justify-center gap-2 rounded-md border bg-white px-3 py-2 shadow-lg drop-shadow-md">
              <span className="font-domaine-display flex items-center justify-center bg-linear-(--primary-dark-gradient) bg-clip-text text-xl leading-none font-bold text-transparent">
                4
              </span>
              <span className="text-neutral-black flex items-center justify-center text-xs font-bold">
                {t("newFeatureStepFour")}
              </span>
            </div>
            <img src={Step4} alt="New feature step 4" className="max-w-96" />
          </div>
          <div className="flex flex-col items-center justify-center gap-10 px-12 py-3">
            <div className="border-primary-light-solid z-10 flex items-center justify-center gap-2 rounded-md border bg-white px-3 py-2 shadow-lg drop-shadow-md">
              <span className="font-domaine-display flex items-center justify-center bg-linear-(--primary-dark-gradient) bg-clip-text text-xl leading-none font-bold text-transparent">
                5
              </span>
              <span className="text-neutral-black flex items-center justify-center text-xs font-bold">
                {t("newFeatureStepFive")}
              </span>
            </div>
            <img src={Step5} alt="New feature step 5" />
          </div>
        </Slider>
      </div>
    </div>
  );
};

export default NewFeatureSection;
